import type { Opponent } from '../game/opponent';

/**
 * Lien P2P établi avec l'ami : messages du jeu (canal fiable et ordonné),
 * maillage du visage (canal non ordonné), flux audio/vidéo optionnel dans
 * chaque sens (chacun choisit ce qu'il envoie de lui-même).
 */
export interface PeerLink extends Opponent {
  sendMesh(data: ArrayBuffer): void;
  onMesh(listener: (data: ArrayBuffer) => void): () => void;
  /** Flux envoyé au pair ; `null` : rien (ou plus rien). */
  setOutgoingStream(stream: MediaStream | null): void;
  /** Flux reçu du pair ; `null` quand il arrête d'en envoyer. */
  onRemoteStream(listener: (stream: MediaStream | null) => void): () => void;
  isOpen(): boolean;
  /** Connexion obtenue : directe ou via un relais TURN (`null` : pas encore connu). */
  connectionKind?(): Promise<'direct' | 'relay' | null>;
}

export type { SignalingState } from './reconnector';
import type { SignalingState } from './reconnector';

/** État de la connexion au serveur de mise en relation (pas au pair). */
export interface SignalingStatus {
  readonly state: SignalingState;
  onChange(listener: (state: SignalingState) => void): () => void;
}

export interface HostSession {
  readonly roomId: string;
  /** Tant que l'ami n'est pas arrivé, le lien n'est valable que si l'hôte est « en ligne ». */
  readonly signaling: SignalingStatus;
  /** Attend l'ami (le premier qui se connecte ; les suivants sont refusés). */
  waitForGuest(): Promise<PeerLink>;
  close(): void;
}

/**
 * Mise en relation. Implémentation V1 : PeerJS (broker public par défaut,
 * serveur local en e2e). Remplaçable sans toucher au jeu.
 */
export interface Connector {
  host(roomId: string): Promise<HostSession>;
  join(roomId: string): Promise<PeerLink>;
}

export type ConnectionErrorCode =
  'roomNotFound' | 'roomTaken' | 'brokerUnreachable' | 'timeout' | 'unsupported';

export class ConnectionError extends Error {
  constructor(
    readonly code: ConnectionErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'ConnectionError';
  }
}
